// Download-as-PDF for the Sheets views (Tab for Joey Forslund, Nov 2 2026 7:01 PM ET: "give me the ability for the webinar
// projections sheets view and the book call funnel projections sheets view to download as a PDF").
// Client-side, no print dialog: walks the rendered sheet (every <table>, the summary cards marked data-pdf-cards and notes marked
// data-pdf-note) and redraws it with jsPDF + jspdf-autotable on US Letter landscape. Cell text, yellow input values, status colors and
// header bands are read from the live DOM, so the PDF is exactly what the sheet shows (current warm / cold set, inputs and results).
// Rows never split across pages (rowPageBreak "avoid"), table headers repeat on every page, column widths follow each table's
// <colgroup> scaled to the page width, so nothing is clipped. Sections can be hidden (display:none) — text is read from the DOM tree.
import jsPDF from "jspdf";
import autoTable, { type CellDef, type RowInput } from "jspdf-autotable";

export type SheetPdfSection = { title: string; root: HTMLElement | null };
export type SheetPdfOptions = { title: string; subtitle: string[]; sections: SheetPdfSection[]; filename: string };

type RGB = [number, number, number];
const BRAND: RGB = [31, 41, 55];

/** Standard PDF fonts only carry Latin-1: map the symbols the sheets use, drop emoji / anything else. */
export function pdfSafe(s: string): string {
  return s
    .replace(/[\u2013\u2014\u2212]/g, "-").replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"').replace(/\u2026/g, "...")
    .replace(/\u2265/g, ">=").replace(/\u2264/g, "<=").replace(/\u2192/g, "->").replace(/\u2190/g, "<-").replace(/[\u25B2\u25BC\u2191\u2193] ?/g, "").replace(/\u2713|\u2714/g, "OK").replace(/\u00D7/g, "x").replace(/\u00F7/g, "/").replace(/\u2248/g, "~")
    .replace(/[\u2022\u25CF]/g, "\u00B7").replace(/\u00A0|\u202F|\u2009/g, " ")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\u00FF]/gu, "")
    .replace(/[ \t]+/g, " ").replace(/ *\n */g, "\n").replace(/\n{2,}/g, "\n").trim();
}

function parseColor(v: string | null | undefined): { rgb: RGB; a: number } | null {
  if (!v) return null;
  const m = v.match(/rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)/);
  if (!m) return null;
  let a = m[4] === undefined ? 1 : m[4].endsWith("%") ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  if (!Number.isFinite(a)) a = 1;
  return { rgb: [Math.round(Number(m[1] ?? 0)), Math.round(Number(m[2] ?? 0)), Math.round(Number(m[3] ?? 0))], a };
}
const isGradient = (el: Element) => { const cs = getComputedStyle(el); return /gradient/.test(cs.backgroundImage) || /gradient|var\(/.test((el as HTMLElement).style?.background || ""); };

/** Visible words of an element: inputs as their typed value, selects as the chosen option, buttons / data-pdf-skip dropped. */
function textOf(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as HTMLElement;
  const tag = el.tagName;
  if (el.hasAttribute("data-pdf-skip") || tag === "BUTTON" || tag === "SCRIPT" || tag === "STYLE" || tag === "svg" || tag === "SVG") return "";
  if (tag === "INPUT") { const i = el as HTMLInputElement; return i.type === "checkbox" || i.type === "radio" ? (i.checked ? "[x]" : "[ ]") : i.value; }
  if (tag === "SELECT") { const s = el as HTMLSelectElement; return s.selectedOptions[0]?.textContent ?? s.value; }
  if (tag === "TEXTAREA") return (el as HTMLTextAreaElement).value;
  if (tag === "BR") return "\n";
  if (el.dataset.inputTag) return `(${el.dataset.inputTag}) `;
  let out = "";
  el.childNodes.forEach((ch) => { out += textOf(ch); });
  if (/^(DIV|P|LI|TR)$/.test(tag)) out = `${out}\n`;
  return out;
}

function cellDef(el: HTMLTableCellElement, rowBg: RGB | null, headerRow: boolean): CellDef {
  const cs = getComputedStyle(el);
  // A status word colored on an inner <span> (Hit / Close / Missed) keeps its color when the cell itself sets none.
  const inner = !el.style.color ? Array.from(el.querySelectorAll<HTMLElement>("[style]")).find((x) => x.style.color && x.tagName !== "INPUT") : undefined;
  const color = parseColor(inner ? getComputedStyle(inner).color : cs.color);
  const bgc = parseColor(cs.backgroundColor);
  const grad = headerRow || isGradient(el);
  const fill: RGB | null = grad ? BRAND : bgc && bgc.a > 0.05 ? bgc.rgb : rowBg;
  let text = pdfSafe(textOf(el));
  if (el.querySelector("svg") && !text) text = "";
  const weight = parseInt(cs.fontWeight, 10);
  const textColor: RGB = grad ? [255, 255, 255] : color ? color.rgb : [32, 33, 36];
  return {
    content: text,
    colSpan: el.colSpan > 1 ? el.colSpan : undefined,
    rowSpan: el.rowSpan > 1 ? el.rowSpan : undefined,
    styles: {
      fillColor: fill ?? [255, 255, 255],
      textColor,
      fontStyle: grad || weight >= 600 ? "bold" : "normal",
      halign: cs.textAlign === "right" || cs.textAlign === "end" ? "right" : cs.textAlign === "center" ? "center" : "left",
    },
  };
}

function rowsOf(section: HTMLTableSectionElement | null, headerRows: boolean): RowInput[] {
  if (!section) return [];
  return Array.from(section.rows).map((tr) => {
    const bg = parseColor(getComputedStyle(tr).backgroundColor);
    const rowBg: RGB | null = isGradient(tr) ? BRAND : bg && bg.a > 0.05 ? bg.rgb : null;
    return Array.from(tr.cells).map((td) => cellDef(td, rowBg, headerRows || isGradient(tr)));
  });
}

function colWidths(table: HTMLTableElement, pageWidth: number): Record<number, { cellWidth: number }> | undefined {
  const cols = Array.from(table.querySelectorAll(":scope > colgroup > col")) as HTMLTableColElement[];
  const px = cols.map((c) => parseFloat(c.style.width || c.getAttribute("width") || ""));
  if (!px.length || px.some((v) => !Number.isFinite(v) || v <= 0)) return undefined;
  const total = px.reduce((a, b) => a + b, 0);
  return Object.fromEntries(px.map((v, i) => [i, { cellWidth: (v / total) * pageWidth }]));
}

export function downloadSheetsPdf({ title, subtitle, sections, filename }: SheetPdfOptions): { pages: number } {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const M = 28;
  const W = doc.internal.pageSize.getWidth() - M * 2;
  const H = doc.internal.pageSize.getHeight();
  let y = M;
  const ensure = (need: number) => { if (y + need > H - M) { doc.addPage(); y = M; } };
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(17, 24, 39);
  doc.text(pdfSafe(title), M, y + 12); y += 22;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(75, 85, 99);
  for (const line of subtitle.map(pdfSafe).filter(Boolean)) { const parts = doc.splitTextToSize(line, W) as string[]; doc.text(parts, M, y + 8); y += parts.length * 11; }
  y += 6;
  sections.forEach((sec, si) => {
    if (!sec.root) return;
    if (si > 0) { doc.addPage(); y = M; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(17, 24, 39);
    doc.text(pdfSafe(sec.title), M, y + 10); y += 18;
    const blocks = Array.from(sec.root.querySelectorAll("table, [data-pdf-cards], [data-pdf-note]")).filter((el) => !el.parentElement?.closest("table, [data-pdf-cards], [data-pdf-note]"));
    for (const el of blocks) {
      if (el.matches("[data-pdf-note]")) {
        const t = pdfSafe(textOf(el)); if (!t) continue;
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(75, 85, 99);
        const parts = doc.splitTextToSize(t, W) as string[]; ensure(parts.length * 10 + 4);
        doc.text(parts, M, y + 7); y += parts.length * 10 + 6;
        continue;
      }
      if (el.matches("[data-pdf-cards]")) {
        const cards = Array.from(el.children).map((c) => pdfSafe(Array.from(c.children).map((x) => textOf(x)).join("\n") || textOf(c))).filter(Boolean);
        if (!cards.length) continue;
        autoTable(doc, {
          startY: y, margin: { left: M, right: M }, theme: "grid", tableWidth: W, body: [cards.map((t) => {
            const [label, ...rest] = t.split("\n");
            return { content: `${(label ?? "").toUpperCase()}\n${rest.join("\n")}`, styles: { fontSize: 9, fillColor: [243, 246, 255] as RGB, textColor: [17, 24, 39] as RGB, cellPadding: 5 } };
          })],
          styles: { lineColor: [199, 210, 254], lineWidth: 0.5, overflow: "linebreak" },
        });
        // @ts-expect-error lastAutoTable is added by jspdf-autotable
        y = (doc.lastAutoTable?.finalY ?? y) + 8;
        continue;
      }
      const table = el as HTMLTableElement;
      const head = rowsOf(table.tHead, true);
      const body = Array.from(table.tBodies).flatMap((b) => rowsOf(b, false));
      const foot = rowsOf(table.tFoot, false);
      if (!head.length && !body.length) continue;
      ensure(60);
      autoTable(doc, {
        startY: y, margin: { left: M, right: M, top: M, bottom: M }, tableWidth: W, theme: "grid",
        head, body, foot, showHead: "everyPage", showFoot: "lastPage", rowPageBreak: "avoid",
        styles: { font: "helvetica", fontSize: 7, cellPadding: 3, overflow: "linebreak", valign: "middle", lineColor: [211, 211, 211], lineWidth: 0.4 },
        headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: "bold" },
        footStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
        columnStyles: colWidths(table, W),
      });
      // @ts-expect-error lastAutoTable is added by jspdf-autotable
      y = (doc.lastAutoTable?.finalY ?? y) + 10;
    }
  });
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p); doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(140, 140, 140);
    doc.text(pdfSafe(`${title} · page ${p} of ${pages}`), M, H - 12);
  }
  doc.save(filename);
  return { pages };
}

/** "Mon, Nov 2, 2026, 7:15 PM ET" (Urban Unity runs on Eastern time), for the PDF subtitle. */
export const pdfGeneratedAt = () => `${new Date().toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })} ET`;
/** "Oct 6 – Nov 2, 2026" from the dashboard's API dates (YYYY-MM-DD); the all-time range starts in 2000. */
export function pdfRangeText(start: string, end: string): string {
  const d = (s: string) => { const [y, m, day] = s.split("-").map(Number); return new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, day ?? 1)); };
  const f = (s: string, withYear = true) => d(s).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });
  if (!start || !end) return "";
  if (start <= "2000-12-31") return `all time (through ${f(end)})`;
  return start.slice(0, 4) === end.slice(0, 4) ? `${f(start, false)} – ${f(end)}` : `${f(start)} – ${f(end)}`;
}
export const pdfFileDate = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
